/**
 * LocalizationSyncService
 *
 * Implements the hash-based localization update strategy agreed with the team:
 *
 *   1. App ships with bundled translations and their hash (BUNDLED_LOCALIZATION_HASH).
 *   2. Every backend API response optionally carries `localization_hash` — the
 *      hash of the latest dictionary version in the database (cached via @lru_cache).
 *   3. This service compares the hashes on each API response.
 *   4. On a mismatch, it fetches the fresh bundle from
 *      GET /api/v1/localization/bundle?locale=<locale>.
 *   5. In-memory overrides are applied on top of the bundled translations in
 *      I18nContext, so no rebuild is required.
 *
 * The service is a singleton to avoid duplicate fetches.
 */

import { BUNDLED_LOCALIZATION_HASH, type SupportedLocale } from "@chinese-vendor/i18n";

// Lazily imported to avoid circular dependency: localizationApi → apiClient → localizationSync
type FetchBundle = (
  locale: SupportedLocale
) => Promise<Record<string, string> | null | undefined>;

export type LocalizationOverrides = Record<string, string>;
export type OverrideListener = (
  locale: SupportedLocale,
  overrides: LocalizationOverrides | null | undefined
) => void;

function normalizeOverrides(
  overrides: LocalizationOverrides | null | undefined
): LocalizationOverrides {
  return overrides && typeof overrides === "object" ? overrides : {};
}

export class LocalizationSyncService {
  /**
   * Per-locale map of the last successfully synced hash.
   * A locale's hash starts as BUNDLED_LOCALIZATION_HASH (implicit, via default).
   * Tracking per-locale prevents a sync for locale A from silently suppressing
   * an overdue sync for locale B that carries the same apiHash.
   */
  private syncedHashByLocale: Partial<Record<SupportedLocale, string>> = {};

  /** In-memory overrides per locale, fetched after a hash mismatch. */
  private overrides: Partial<Record<SupportedLocale, LocalizationOverrides>> = {};

  /** Whether a fetch is already in flight (deduplicate concurrent calls). */
  private fetching = false;

  private listeners: OverrideListener[] = [];

  // ── Dependency injection ────────────────────────────────────────────────────
  // The actual fetch function is registered at runtime to break the circular
  // dependency cycle: apiClient → localizationSync → localizationApi → apiClient.

  private _fetchBundle: FetchBundle | null = null;

  registerFetchBundle(fn: FetchBundle): void {
    this._fetchBundle = fn;
  }

  // ── Subscription ────────────────────────────────────────────────────────────

  subscribe(listener: OverrideListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(locale: SupportedLocale, overrides: LocalizationOverrides | null | undefined): void {
    this.listeners.forEach((fn) => fn(locale, overrides));
  }

  // ── Public API ───────────────────────────────────────────────────────────────

  /**
   * Called by the API client after every response that carries a
   * `localization_hash`. If the hash differs from the current one, a fresh
   * bundle is fetched for `locale` and listeners are notified.
   */
  async checkAndSync(apiHash: string | null, locale: SupportedLocale): Promise<void> {
    // Compare against this locale's last synced hash (or the bundled hash as default).
    const localeSyncedHash = this.syncedHashByLocale[locale] ?? BUNDLED_LOCALIZATION_HASH;
    if (!apiHash || apiHash === localeSyncedHash) return;
    if (this.fetching) return; // In-flight deduplication
    if (!this._fetchBundle) {
      console.warn("[LocalizationSync] fetchBundle not registered — skipping sync");
      return;
    }

    this.fetching = true;
    try {
      const entries = normalizeOverrides(await this._fetchBundle(locale));
      this.overrides[locale] = entries;
      this.syncedHashByLocale[locale] = apiHash;
      this.emit(locale, entries);
    } catch (err) {
      console.warn("[LocalizationSync] Failed to fetch updated bundle:", err);
    } finally {
      this.fetching = false;
    }
  }

  /**
   * Returns the current in-memory overrides for a locale.
   * Returns an empty object if no overrides have been fetched yet.
   */
  getOverrides(locale: SupportedLocale): LocalizationOverrides {
    return this.overrides[locale] ?? {};
  }

  /** Exposed for testing. */
  get bundledHash(): string {
    return BUNDLED_LOCALIZATION_HASH;
  }
}

export const localizationSyncService = new LocalizationSyncService();
